import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@components/base'
import { TabView, useActionBottomSheet } from '@components'
import { NavigationContainer } from '@screens/Common/containers'
import { LocalMeal as Meal } from '@src/screens/Events/models/events.types'
import { MealEditor, MealsList } from '@screens/Meals'
import { copyMeal, getDraftMeal, hasUnsavedChanges } from '@screens/Meals/utils'
import { favoriteMealsSelector, mealDraftSelector, recentMealsSelector } from '@selectors/meals'
import { useGoBack } from '@utils/navigation'

const newMealIcon = { pack: 'eva', name: 'close' } as const

export const MealEditorWrapper = () => {
  const styles = useStyleSheet(themedStyle)
  const dispatch = useDispatch()
  const goBack = useGoBack()

  const [tabIndex, setTabIndex] = useState(0)

  const draftMeal = useSelector(mealDraftSelector)
  const initialFavorites = useSelector(favoriteMealsSelector)
  const recents = useSelector(recentMealsSelector)
  const showActionBottomSheet = useActionBottomSheet()

  // List to keep the initial favorite items. All initial favorite meals
  // should be displayed for the current meal session, even when they are toggled off
  const [favorites, setFavorites] = useState(initialFavorites)

  const onCancelEditConfirm = useCallback(() => {
    dispatch({ type: 'meals/saveDraft' })
    goBack()
  }, [dispatch, goBack])

  const onCancelEdit = useCallback(() => {
    if (!hasUnsavedChanges(draftMeal)) {
      dispatch({ type: 'meals/saveDraft' })
      return goBack()
    }

    showActionBottomSheet({
      body: (
        <Text bold type="large" style={styles.cancelEditText}>
          Are you sure you want to discard changes to this meal?
        </Text>
      ),
      primaryButton: {
        text: 'Discard Changes',
        onPress: onCancelEditConfirm,
      },
    })
  }, [
    dispatch,
    draftMeal,
    goBack,
    onCancelEditConfirm,
    showActionBottomSheet,
    styles.cancelEditText,
  ])

  const cancelNewMeal = useCallback(() => {
    dispatch({ type: 'meals/saveDraft' })
    goBack()
  }, [dispatch, goBack])

  const onCancelNewMeal = useCallback(() => {
    dispatch({
      type: 'meals/saveDraft',
      payload: hasUnsavedChanges(draftMeal) ? { ...draftMeal, hasUnsavedChanges: true } : {},
    })

    if (!hasUnsavedChanges(draftMeal)) {
      return goBack()
    }

    showActionBottomSheet({
      body: (
        <Text bold type="large" style={styles.cancelEditText}>
          Do you really want to cancel the new meal?
        </Text>
      ),
      primaryButton: {
        text: 'Yes, Cancel It',
        onPress: cancelNewMeal,
      },
    })
  }, [cancelNewMeal, dispatch, draftMeal, goBack, showActionBottomSheet, styles.cancelEditText])

  const onToggleFavorite = useCallback(
    (item: Meal) => {
      setFavorites((prevFavorites) => {
        const isExistingFavorite = prevFavorites.some((meal) => meal.id === item.id)

        if (!isExistingFavorite) {
          return [{ ...item, favorite: true }, ...prevFavorites]
        }

        return prevFavorites.map((meal) =>
          meal.id === item.id ? { ...meal, favorite: !meal.favorite } : meal,
        )
      })

      dispatch({
        type: 'meals/updateFavorite',
        payload: {
          ...item,
          favorite: !item.favorite,
          time: item.occurredAt, // `time` field is required until BE is updated
        },
      })
    },
    [dispatch],
  )

  const onItemSelected = useCallback(
    (item: Meal) => {
      dispatch({
        type: 'meals/updateDraft',
        payload: copyMeal(getDraftMeal(item)),
      })
      setTabIndex(0)
    },
    [dispatch, setTabIndex],
  )

  const isNewMeal = !draftMeal.id

  const handleGoBack = useCallback(() => {
    if (isNewMeal) {
      onCancelNewMeal()
    } else {
      onCancelEdit()
    }
  }, [isNewMeal, onCancelEdit, onCancelNewMeal])

  const favoritesEmptyListIcon = useMemo(() => <Icon name="heart" style={styles.heartIcon} />, [
    styles.heartIcon,
  ])

  const recentsEmptyListIcon = useMemo(() => <Icon name="clock" style={styles.clockIcon} />, [
    styles.clockIcon,
  ])

  return (
    <NavigationContainer
      title={isNewMeal ? 'Add Meal' : 'Edit Meal'}
      goBack={handleGoBack}
      leftIcon={isNewMeal ? newMealIcon : undefined}
    >
      {isNewMeal ? (
        <TabView
          swipeEnabled
          tabIndex={tabIndex}
          onChangeTabIndex={setTabIndex}
          routes={[
            {
              key: 'new-meal',
              title: 'New Meal',
              sceneComponent: <MealEditor />,
            },
            {
              key: 'favorite-meals',
              title: 'Favorites',
              sceneComponent: (
                <MealsList
                  items={favorites}
                  emptyListText={
                    'Your favorite meals will appear here.\n' +
                    "Press the heart icon on the meal you'd like to favorite and it'll appear here."
                  }
                  emptyListIcon={favoritesEmptyListIcon}
                  onItemSelected={onItemSelected}
                  onToggleFavorite={onToggleFavorite}
                />
              ),
            },
            {
              key: 'recent-meals',
              title: 'Recents',
              sceneComponent: (
                <MealsList
                  items={recents}
                  emptyListIcon={recentsEmptyListIcon}
                  emptyListText={
                    "Meals you're logging will appear here.\n" +
                    'Start logging your meals to get them here.'
                  }
                  onItemSelected={onItemSelected}
                  onToggleFavorite={onToggleFavorite}
                />
              ),
            },
          ]}
        />
      ) : (
        <MealEditor />
      )}
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  heartIcon: {
    color: 'theme.like',
    width: 48,
  },
  clockIcon: {
    color: 'theme.text.secondary',
    width: 48,
  },
  cancelEditText: {
    textAlign: 'center',
  },
})
